
import { createContext, useContext, useState, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { restaurantService } from '../services/restaurant.service';
import { useAuth } from './AuthContext';
import { useTranslation } from 'react-i18next';
import { DEFAULT_CURRENCY } from '../config/branding';

interface POSContextType {
    restaurantId: string | null;
    posId: string | null;
    restaurantName: string;
    loading: boolean;
    error: string | null;
    resolveStatus: 'idle' | 'resolving' | 'resolved' | 'failed';
    currency?: string;
    language?: string;
    smdf_url?: string | null;
    smdf_gateway_id?: string | null;
    imdf?: string | null;
    country?: string | null;
    show_product_images?: boolean;
    receipt_print_mode?: 'always' | 'never' | 'ask';
    smdfOfflineTickets: number | null;
    setSmdfOfflineTickets: (n: number | null) => void;
    onboardingStatus: {
        step1: boolean;
        step2: boolean;
        step3: boolean;
        step4: boolean;
        isComplete: boolean;
    };
}

const POSContext = createContext<POSContextType | undefined>(undefined);

export const POSProvider = ({ children }: { children: ReactNode }) => {
    // Hooks must ALWAYS be called unconditionally - this is a fundamental React rule
    const params = useParams<{ restaurantId?: string }>();
    const { user } = useAuth();

    const [restaurantId, setRestaurantId] = useState<string | null>(null);
    const [restaurantName, setRestaurantName] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [posId] = useState<string | null>(() => sessionStorage.getItem('current_pos_id'));

    const [resolveStatus, setResolveStatus] = useState<'idle' | 'resolving' | 'resolved' | 'failed'>('idle');
    const [currency, setCurrency] = useState(DEFAULT_CURRENCY);
    const [language, setLanguage] = useState('fr');
    const [smdfUrl, setSmdfUrl] = useState<string | null>(null);
    const [smdfGatewayId, setSmdfGatewayId] = useState<string | null>(null);
    const [imdf, setImdf] = useState<string | null>(null);
    const [country, setCountry] = useState<string | null>(null);
    const [showProductImages, setShowProductImages] = useState(true);
    const [receiptPrintMode, setReceiptPrintMode] = useState<'always' | 'never' | 'ask'>('always');
    const [smdfOfflineTickets, setSmdfOfflineTickets] = useState<number | null>(null);
    const [onboardingStatus, setOnboardingStatus] = useState({
        step1: true,
        step2: false,
        step3: false,
        step4: false,
        isComplete: false,
    });

    useEffect(() => {
        resolveContext();
    }, [params?.restaurantId, user?.id]); // Refined dependencies: Only trigger on URL param change or user change

    // Sync language with i18n. Tracks the last value *we* requested rather than
    // comparing to i18n.language directly: changeLanguage() can resolve to a
    // normalized form (region/case) that never matches the raw `language` string,
    // and each completed changeLanguage() emits 'languageChanged', which
    // useTranslation() turns into a forced re-render — re-running this effect
    // forever if the comparison can never become true (React error #185).
    const { i18n } = useTranslation();
    const appliedLanguageRef = useRef<string | null>(null);
    useEffect(() => {
        if (language && appliedLanguageRef.current !== language) {
            appliedLanguageRef.current = language;
            i18n.changeLanguage(language);
        }
    }, [language, i18n]);

    const fetchRestaurantDetails = async (id: string, isLookup: boolean = false): Promise<boolean> => {
        try {
            console.log(`[POSContext] Fetching details for restaurant ${id}...`);
            const res = isLookup
                ? await restaurantService.getBySubdomain(id)
                : await restaurantService.getByIdPublic(id);

            if (res.data.restaurant) {
                console.log("[POSContext] Details loaded:", res.data.restaurant);
                setRestaurantName(res.data.restaurant.name);
                setCurrency(res.data.restaurant.currency || DEFAULT_CURRENCY);
                setLanguage(res.data.restaurant.language || 'fr');
                setSmdfUrl(res.data.restaurant.smdf_url ?? null);
                setShowProductImages(res.data.restaurant.show_product_images ?? true);
                setReceiptPrintMode(res.data.restaurant.receipt_print_mode ?? 'always');
                setSmdfGatewayId(res.data.restaurant.smdf_gateway_id ?? null);
                setImdf(res.data.restaurant.imdf ?? null);
                setCountry(res.data.restaurant.country ?? null);
                
                // Calculate onboarding status based on relations count
                const counts = res.data.restaurant._count;
                if (counts) {
                    const step2 = counts.payment_methods > 0;
                    const step3 = counts.warehouses > 0;
                    const step4 = counts.point_of_sales > 0;
                    setOnboardingStatus({
                        step1: true,
                        step2,
                        step3,
                        step4,
                        isComplete: step2 && step3 && step4,
                    });
                } else {
                    // Fallback if _count is not available (e.g. some old cached responses)
                    setOnboardingStatus({
                        step1: true, step2: true, step3: true, step4: true, isComplete: true
                    });
                }

                setResolutionSuccess(String(res.data.restaurant.id)); // ALWAYS use the real ID from the server
            }
            return true; // Resolved successfully
        } catch (e: any) {
            // Silently handle 401 if we're not logged in yet
            if (e.response?.status === 401) {
                console.warn("[POSContext] Unauthorized to fetch full details. Proceeding with limited context.");
                return false;
            }

            console.error("[POSContext] Failed to fetch details", e);
            return false; // Could not resolve — let the caller try its other fallbacks
        }
    };

    const setResolutionSuccess = (id: string) => {
        setRestaurantId(id);
        setResolveStatus('resolved');
        setLoading(false);
    }

    const resolveContext = async () => {
        // Determine the target identifier first
        let identifier: string | null = params.restaurantId || null;
        let isLookup = !identifier || isNaN(Number(identifier));

        if (!identifier) {
            // Check Subdomain — but an IPv4 address also splits on dots
            // (e.g. "192.168.1.7".split('.')[0] === "192"), so a LAN-IP
            // deployment would otherwise be misread as a subdomain/slug.
            const hostname = window.location.hostname;
            const isIpAddress = /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname);
            const parts = hostname.split('.');
            if (!isIpAddress && (parts.length > 2 || (hostname.includes('localhost') && parts.length > 1))) {
                if (parts[0] !== 'www' && parts[0] !== 'app' && parts[0] !== 'admin') {
                    identifier = parts[0];
                    isLookup = true;
                }
            }
        }

        if (!identifier) {
            identifier = localStorage.getItem('pos_restaurant_id');
            isLookup = false;
        }

        // If already resolved (or currently resolving), skip redundant fetch.
        // The numeric restaurantId from the DB never matches a slug identifier
        // (e.g. "5" !== "le-petit-pecheur"), so the old string-equality check
        // always failed and triggered a new network call on every user?.id change.
        if (resolveStatus === 'resolved' && restaurantId) {
            return;
        }
        if (resolveStatus === 'resolving') {
            return;
        }

        setResolveStatus('resolving');
        setLoading(true);
        setError(null);

        try {
            // 1. URL Param or Subdomain or LocalStorage
            if (identifier) {
                const found = await fetchRestaurantDetails(identifier, isLookup);
                if (found) return;
            }

            // 1b. Subdomain lookup failed (e.g. the URL's subdomain no longer
            // matches this restaurant's slug/subdomain in DB) — a previously
            // cached id from a past successful resolution is still worth a try
            // before giving up. Skipped when step 1 already used this same value
            // (the !isLookup / no-identifier path already reads localStorage).
            if (isLookup) {
                const cachedId = localStorage.getItem('pos_restaurant_id');
                if (cachedId) {
                    const found = await fetchRestaurantDetails(cachedId, false);
                    if (found) return;
                }
            }

            // 2. User Context (Last Resort)
            let inferredRestaurantId: string | null = null;
            if (user) {
                if (user.restaurant_id) {
                    inferredRestaurantId = String(user.restaurant_id);
                }
                else if (user.employees && Array.isArray(user.employees) && user.employees.length > 0) {
                    const emp = user.employees[0];
                    if (emp.restaurant_id) {
                        inferredRestaurantId = String(emp.restaurant_id);
                    }
                }
                else if (user.partners) {
                    const partnersList = Array.isArray(user.partners) ? user.partners : [user.partners];
                    for (const p of partnersList) {
                        if (p.restaurants && Array.isArray(p.restaurants) && p.restaurants.length > 0) {
                            inferredRestaurantId = String(p.restaurants[0].id);
                            break;
                        }
                    }
                }
            }

            if (inferredRestaurantId) {
                console.log(`[POSContext] Resolved restaurant context from user: ${inferredRestaurantId}`);
                await fetchRestaurantDetails(String(inferredRestaurantId), false);
                return;
            }

            // Failed to resolve
            setResolveStatus('failed');
            setError('Impossible de déterminer le restaurant. Veuillez utiliser un lien valide.');
        } catch (err: any) {
            setError(err.message || 'Erreur de contexte restaurant');
            setResolveStatus('failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <POSContext.Provider value={{ restaurantId, posId, restaurantName, loading, error, resolveStatus, currency, language, smdf_url: smdfUrl, smdf_gateway_id: smdfGatewayId, imdf, country, show_product_images: showProductImages, receipt_print_mode: receiptPrintMode, smdfOfflineTickets, setSmdfOfflineTickets, onboardingStatus }}>
            {children}
        </POSContext.Provider>
    );
};

export const usePOS = () => {
    const context = useContext(POSContext);
    if (context === undefined) {
        throw new Error('usePOS must be used within a POSProvider');
    }
    return context;
};
